/**
 * Everything one client can reach, on one sheet (spec 25 / increment 03c-iii).
 *
 * The point of the whole spec arrives here: onboarding a client stops being an env edit plus two
 * redeploys and becomes six controls an admin operates. Every endpoint increments 03a-04a built was
 * reachable only by curl until this file.
 *
 * Two rules this component exists to enforce, both from the design review:
 *
 * READ-ONLY ON LOAD. The initial render issues GETs and nothing else. A panel that seeded, or repaired,
 * or normalised anything as a side effect of being looked at would write configuration attributed to
 * whoever opened the tab.
 *
 * RE-READ AFTER EVERY WRITE, never patch local state. The server invalidates its entitlements cache in
 * the same statement that writes the audit row; this component's own view is the SECOND cache. Patching
 * optimistically leaves the displayed configuration disagreeing with the enforced one, which is the
 * failure mode where an operator swears they granted something and the client still cannot see it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as api from './api';
import { AuditSurface } from './components/AuditSurface';
import { DomainsSurface } from './components/DomainsSurface';
import { GrantsSurface } from './components/GrantsSurface';
import { NewOrgForm } from './components/NewOrgForm';
import { SharingSurface } from './components/SharingSurface';
import { UseCasesSurface } from './components/UseCasesSurface';
import type { PickerOption } from './components/ResourcePicker';
import {
  CHAT_AUDIENCES_ADD,
  CHAT_AUDIENCES_HINT,
  CHAT_AUDIENCES_SUBMIT,
  CHAT_AUDIENCES_TITLE,
  CHAT_AUDIENCES_WHY,
  CHAT_SEGMENTS_ADD,
  CHAT_SEGMENTS_SUBMIT,
  CHAT_SEGMENTS_TITLE,
  CHAT_SEGMENTS_WHY,
  NO_CHAT_READY_LABEL,
  chatReadyLabel,
} from './chatGrantCopy';
import type {
  AuditEntry,
  CatalogueResponse,
  Grant,
  OrgDetail,
  OrgListResponse,
  ResourceType,
  SegmentsResponse,
} from './types';

/**
 * The use cases the panel offers to tick.
 *
 * Hardcoded HERE and nowhere else in the frontend, because the backend has no catalogue endpoint for
 * them yet — `USE_CASE_FEATURE_MAP` is a Python dict. That is a real seam and it is recorded rather than
 * hidden: a use case set on an org but absent from this list still renders (UseCasesSurface unions the
 * two), so the gap degrades to "not offered" and never to "invisible and unremovable".
 */
const USE_CASE_CATALOGUE = [
  'survey-and-focus-groups',
  'concept-test',
  'vitamin-concept-test',
  'driving-concept-test',
  'packaging-review-v2',
  'packaging-variant-comparison',
  'claims-testing',
  'attribution',
  'channel-strategy',
  'image-survey',
  'healthcare-chat-v2',
  'bluepill-chat',
  'hbs-chat',
];

const AUDIT_PAGE = 25;

export function AccessDashboard() {
  const [orgs, setOrgs] = useState<OrgListResponse | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [segments, setSegments] = useState<SegmentsResponse | null>(null);
  const [catalogue, setCatalogue] = useState<CatalogueResponse | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * A refresh that FAILED, so the rows on screen are older than the database.
   *
   * Separate from `loadError`, which is the first-load failure and replaces the whole view. This one
   * happens when there is already something rendered, and that is what makes it dangerous: without it,
   * a re-read that answers 500 leaves the pre-write rows in place with no signal, so a write that
   * SUCCEEDED looks like one that did nothing and the operator's next move is to repeat it.
   *
   * Measured, not theorised: a real detach against DEV committed, its re-read hit a dead pooled
   * connection and 500'd, and the panel showed the domain still attached. Every loader guarded its
   * setState with `if (data)` — which turns a failed refresh into silence by construction.
   */
  const [staleSince, setStaleSince] = useState<string | null>(null);

  /** The reference data — fetched once, and again only when a write could have changed it. */
  const loadContext = useCallback(async () => {
    const [segs, cat, log] = await Promise.all([
      api.fetchSegments(),
      api.fetchCatalogue(),
      api.fetchAudit(AUDIT_PAGE),
    ]);
    if (segs.data) setSegments(segs.data);
    if (cat.data) setCatalogue(cat.data);
    if (log.data) setAudit(log.data.entries);
    // The audit log is the one an operator checks to confirm a change landed, so a stale one is worse
    // than an empty one.
    const failure = log.error || segs.error || cat.error;
    setStaleSince((current) => failure || current);
    return failure ?? null;
  }, []);

  const loadOrgs = useCallback(async () => {
    const { data, error } = await api.fetchOrganizations();
    if (error) {
      setLoadError(error);
      setStaleSince(error);
      return error;
    }
    setOrgs(data);
    // Select the first org so the sheet is never an empty right-hand pane on arrival. Only when
    // nothing is selected yet — re-selecting on every reload would throw the operator back to the top
    // of the list after each write.
    setSelectedId(
      (current) =>
        current ?? (data && data.organizations.length > 0 ? data.organizations[0].id : null),
    );
    return null;
  }, []);

  const loadDetail = useCallback(async (id: number) => {
    const { data, error } = await api.fetchOrganization(id);
    if (error) {
      setLoadError(error);
      setStaleSince(error);
      return error;
    }
    setDetail(data);
    return null;
  }, []);

  // Both effects run their work inside a nested async function. Not a lint dodge: every setState in
  // these loaders happens AFTER an await, so none of them runs during the effect's synchronous pass,
  // and the nesting is what makes that true rather than merely intended. (The sibling enterprise
  // dashboard satisfies the same rule only because its fetchers are plain functions the linter cannot
  // trace into — passing by being less analyzable, which is not the same as being correct.)
  useEffect(() => {
    const load = async () => {
      await Promise.all([loadOrgs(), loadContext()]);
    };
    void load();
  }, [loadOrgs, loadContext]);

  useEffect(() => {
    if (selectedId === null) return;
    const load = async () => {
      await loadDetail(selectedId);
    };
    void load();
  }, [selectedId, loadDetail]);

  /**
   * Run a write, then re-read. Returns the server's error text, or null.
   *
   * Every mutation in this component goes through here, which is what makes the re-read structural
   * rather than something each handler has to remember. The reload happens even on failure: a partial
   * write is possible (a grant list can be accepted in part), and after a refusal the operator needs to
   * see what is actually there, not what was there before they tried.
   */
  const write = useCallback(
    async (run: () => Promise<{ error: string | null }>): Promise<string | null> => {
      setBusy(true);
      setStaleSince(null);
      try {
        const { error } = await run();
        // Every refresh is attempted even if an earlier one failed — a partial refresh is still more
        // current than none. Each loader marks the view stale itself, so there is exactly ONE mechanism
        // for that rather than a second aggregation here: a mutation sweep showed an aggregation in this
        // function changed no test, because the loaders had already covered it.
        if (selectedId !== null) await loadDetail(selectedId);
        await loadOrgs();
        await loadContext();
        return error;
      } finally {
        setBusy(false);
      }
    },
    [selectedId, loadDetail, loadOrgs, loadContext],
  );

  /**
   * Create, then select. Landing the operator on the new client's empty sheet is the point: the next
   * thing they need is the domain and the grants, and a create that returns them to the list makes them
   * hunt for the row they just made.
   */
  const createOrg = useCallback(
    async (slug: string, name: string): Promise<string | null> => {
      setBusy(true);
      try {
        const { data, error } = await api.createOrganization(slug, name);
        await Promise.all([loadOrgs(), loadContext()]);
        if (!error && data?.id) setSelectedId(data.id);
        return error;
      } finally {
        setBusy(false);
      }
    },
    [loadOrgs, loadContext],
  );

  const grantsOf = (type: ResourceType): Grant[] =>
    (detail?.grants ?? []).filter((g) => g.resource_type === type);

  const segmentOptions = useMemo<PickerOption[]>(() => {
    const granted = new Set(grantsOf('segment').map((g) => g.resource_id));
    return (segments?.segments ?? []).map((s) => ({
      value: String(s.segment_id),
      primary: s.segment_name || s.configured_label || `Segment ${s.segment_id}`,
      // Id AND audience on the second line: the id is what gets stored, the audience is what proves it
      // is the right one. 753 was granted for two weeks because nothing displayed the pair.
      secondary: [String(s.segment_id), s.audience].filter(Boolean).join(' · '),
      warning:
        s.verdict === 'mismatch'
          ? `configured as "${s.configured_label}" but resolves to "${s.segment_name}"`
          : undefined,
      alreadyGranted: granted.has(s.segment_id),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, detail]);

  const audienceOptions = useMemo<PickerOption[]>(() => {
    const granted = new Set(grantsOf('audience').map((g) => g.resource_id));
    return (catalogue?.audiences ?? []).map((a) => ({
      value: String(a.id),
      primary: a.name,
      secondary: `${a.id} · granted to ${a.granted_to} client${a.granted_to === 1 ? '' : 's'}`,
      alreadyGranted: granted.has(a.id),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, detail]);

  const chatAudienceOptions = useMemo<PickerOption[]>(() => {
    const granted = new Set(grantsOf('chat_audience').map((g) => g.resource_id));
    return (catalogue?.audiences ?? []).map((a) => ({
      value: String(a.id),
      primary: a.name,
      secondary: [
        String(a.id),
        a.segment_count == null
          ? null
          : a.segment_count > 0
            ? chatReadyLabel(a.segment_count)
            : NO_CHAT_READY_LABEL,
      ]
        .filter(Boolean)
        .join(' · '),
      alreadyGranted: granted.has(a.id),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, detail]);

  const populationOptions = useMemo<PickerOption[]>(() => {
    const granted = new Set(grantsOf('population').map((g) => g.resource_key));
    return (catalogue?.populations ?? []).map((p) => ({
      // The NAME is the identity. Populations have no id — there is no populations table, and the only
      // record of a valid name is a grant naming it.
      value: p.name,
      primary: p.name,
      secondary: `granted to ${p.granted_to} client${p.granted_to === 1 ? '' : 's'}`,
      alreadyGranted: granted.has(p.name),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue, detail]);

  if (loadError && !orgs) {
    return (
      <div
        role="alert"
        className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-900"
      >
        {loadError}
      </div>
    );
  }

  if (!orgs) {
    return <p className="p-4 text-sm text-gray-500">Loading access configuration…</p>;
  }

  if (!orgs.seeded) {
    // The state increment 02 ships in: tables created, nothing in them. An empty table here reads as a
    // broken feature, and an operator who concludes that stops using the panel.
    return (
      <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-semibold">Nothing has been seeded yet.</p>
        <p className="mt-1">
          The access tables exist but hold no rows, so there is no configuration to show. Run the
          seed to import the current environment configuration:
        </p>
        <pre className="mt-2 overflow-x-auto rounded bg-white/70 px-2 py-1.5 font-mono text-xs">
          uv run python -m deployments.seed_access_control --check
        </pre>
        <p className="mt-1 text-xs">
          <code>--check</code> writes nothing; re-run with <code>--apply</code> once the preview
          looks right.
        </p>
        <div className="mt-3 border-t border-amber-200 pt-3">
          <p className="mb-2 text-xs">
            Or onboard a client that was never in the env configuration:
          </p>
          <NewOrgForm onCreate={createOrg} busy={busy} />
        </div>
      </div>
    );
  }

  const org = detail?.organization;

  return (
    <div className="space-y-4">
      {staleSince && (
        <div
          data-testid="stale"
          role="alert"
          className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        >
          <b>This view is out of date.</b> The change was sent, but reading the configuration back
          failed, so the rows below may be older than the database — do not assume a change did not
          land. Reload before acting on what you see.
          <span className="mt-1 block font-mono text-xs opacity-80">{staleSince}</span>
        </div>
      )}

      <p className="text-sm text-gray-600">
        Everything one client can reach, on one sheet. Changes take effect immediately, each one
        names the admin who made it, and a mistaken removal can be reverted from the log.
      </p>

      <div className="flex flex-wrap gap-4 text-xs text-gray-600">
        <Stat label="Client orgs" value={orgs.organizations.length} />
        <Stat label="Personal orgs" value={orgs.personal_org_count} />
        <Stat label="Chat segments" value={segments?.segments.length ?? 0} />
        {(segments?.verdict_counts.mismatch ?? 0) > 0 && (
          <Stat
            label="Needs review"
            value={segments?.verdict_counts.mismatch ?? 0}
            hint="a configured label contradicts the audience it resolves to — shown in the segment picker"
            attention
          />
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-[220px_1fr]">
        <nav aria-label="Client organizations" className="rounded border border-gray-200 bg-white">
          <h2 className="border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Organizations
          </h2>
          <ul>
            {orgs.organizations.map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => setSelectedId(o.id)}
                  aria-current={o.id === selectedId}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    o.id === selectedId ? 'bg-teal-50 font-medium text-teal-900' : 'text-gray-800'
                  }`}
                >
                  <span className="block truncate">{o.name}</span>
                  <span className="block truncate text-xs text-gray-500">
                    {o.slug} · {o.grant_count} grant{o.grant_count === 1 ? '' : 's'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-gray-100 p-2">
            <NewOrgForm onCreate={createOrg} busy={busy} />
          </div>
        </nav>

        <div className="rounded border border-gray-200 bg-white">
          {!org ? (
            <p className="p-4 text-sm text-gray-500">Select an organization.</p>
          ) : (
            <>
              <header className="flex flex-wrap items-baseline gap-x-3 border-b border-gray-100 px-4 py-3">
                <h2 className="text-base font-semibold text-gray-900">{org.name}</h2>
                <span className="font-mono text-xs text-gray-500">
                  {org.slug} · id {org.id}
                </span>
                <span className="ml-auto rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                  {org.status}
                </span>
              </header>

              <SharingSurface
                visibility={org.default_simulation_visibility}
                orgName={org.name}
                busy={busy}
                onSet={(visibility) => write(() => api.setSimulationVisibility(org.id, visibility))}
              />

              <UseCasesSurface
                useCases={detail?.use_cases ?? { allowed: [], denied: [] }}
                catalogue={USE_CASE_CATALOGUE}
                busy={busy}
                onSet={(useCase, effect) => write(() => api.setUseCases(org.id, [useCase], effect))}
                onClear={(useCase) => write(() => api.clearUseCase(org.id, useCase))}
              />

              <DomainsSurface
                domains={detail?.domains ?? []}
                orgName={org.name}
                busy={busy}
                onAttach={(domain) => write(() => api.attachDomain(org.id, domain))}
                onDetach={(domain) => write(() => api.detachDomain(org.id, domain))}
              />

              <GrantsSurface
                title={CHAT_SEGMENTS_TITLE}
                why={CHAT_SEGMENTS_WHY}
                grants={grantsOf('segment')}
                options={segmentOptions}
                addLabel={CHAT_SEGMENTS_ADD}
                submitLabel={CHAT_SEGMENTS_SUBMIT}
                keyed="id"
                busy={busy}
                onGrant={(values) =>
                  write(() => api.grantByIds(org.id, 'segment', values.map(Number)))
                }
                onRevoke={(g) => write(() => api.revokeGrant(g.id))}
              />

              <GrantsSurface
                title={CHAT_AUDIENCES_TITLE}
                why={CHAT_AUDIENCES_WHY}
                grants={grantsOf('chat_audience')}
                options={chatAudienceOptions}
                addLabel={CHAT_AUDIENCES_ADD}
                submitLabel={CHAT_AUDIENCES_SUBMIT}
                keyed="id"
                busy={busy}
                footnote={CHAT_AUDIENCES_HINT}
                onGrant={(values) =>
                  write(() => api.grantByIds(org.id, 'chat_audience', values.map(Number)))
                }
                onRevoke={(g) => write(() => api.revokeGrant(g.id))}
              />

              <GrantsSurface
                title="Survey chat · surveys and groups"
                why="Cards on the survey-chat screen."
                grants={[...grantsOf('survey'), ...grantsOf('survey_group')]}
                options={[]}
                addLabel="Add surveys…"
                submitLabel="Grant surveys"
                keyed="id"
                busy={busy}
                emptyHint="No survey-chat targets. Seeded from SURVEY_CHAT_CLIENTS today."
                onGrant={(values) =>
                  write(() => api.grantByIds(org.id, 'survey', values.map(Number)))
                }
                onRevoke={(g) => write(() => api.revokeGrant(g.id))}
              />

              <GrantsSurface
                title="Concept test · populations"
                why="Populations offered when this client runs a concept or packaging test. Keyed by name — there is no populations table."
                grants={grantsOf('population')}
                options={populationOptions}
                addLabel="Add populations…"
                submitLabel="Grant populations"
                keyed="key"
                busy={busy}
                onGrant={(values) => write(() => api.grantByKeys(org.id, 'population', values))}
                onRevoke={(g) => write(() => api.revokeGrant(g.id))}
              />

              <GrantsSurface
                title="Audiences"
                why="Simulation audiences — the same grants that appear under a user's audience list."
                grants={grantsOf('audience')}
                options={audienceOptions}
                addLabel="Add audiences…"
                submitLabel="Grant audiences"
                keyed="id"
                busy={busy}
                onGrant={(values) =>
                  write(() => api.grantByIds(org.id, 'audience', values.map(Number)))
                }
                onRevoke={(g) => write(() => api.revokeGrant(g.id))}
              />

              <AuditSurface
                entries={audit}
                busy={busy}
                onRevert={(entry) => write(() => api.revertAudit(entry.id))}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  attention = false,
}: {
  label: string;
  value: number;
  hint?: string;
  attention?: boolean;
}) {
  return (
    <span
      title={hint}
      className={`rounded border px-2.5 py-1 ${
        attention ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-gray-200 bg-white'
      }`}
    >
      <span className="text-gray-500">{label} </span>
      <b className="tabular-nums">{value}</b>
    </span>
  );
}

export default AccessDashboard;
